/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useState } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import InfoIcon from "@mui/icons-material/Info";
import {
    ShiftEffectiveDateLogic,
    LunchBreakOption,
} from "../../models/calculation/TESCalculationParameters";
import MuiSelect, { IMuiSelectOption } from "../framework/muiSelect";
import { NumberInput } from "../framework/numberInput";
import { CalcGeneralProps } from "./CalculationMain";
import { useTranslation } from "react-i18next";

export const CalcShiftSettings = ({
    translate,
    handleChange,
    settings,
    setSettings,
}: CalcGeneralProps) => {
    const { t } = useTranslation();
    const {
        paidBreak,
        paidBreakShiftMinMinutes,
        paidWaiting,
        shiftEffectiveDateLogic,
        isWorkShiftStartBasedOnPlannedShift,
        isSplitWorkShiftOnSalaryPeriodChange,
        dailyBreakDuration,
        stdShiftDuration,
        lunchBreakOption,
    } = settings || {};

    const [selectedResultRoundingMinutes, setSelectedResultRoundingMinutes] =
        useState<number>(settings.resultRoundingMinutes);
    const [selectedShiftRoundingMinutes, setSelectedShiftRoundingMinutes] =
        useState<number>(settings.shiftRoundingMinutes);

    const roundingOptions: IMuiSelectOption[] = [
        { label: "5 minuuttia", value: 5 },
        { label: "10 minuuttia", value: 10 },
        { label: "15 minuuttia", value: 15 },
        { label: "30 minuuttia", value: 30 },
        { label: "60 minuuttia", value: 60 },
    ];

    const handleLunchOptionChange = (value: LunchBreakOption) => {
        const newSettings = { ...settings };
        newSettings.lunchBreakOption = value;
        newSettings.isPaidLunchBreakOn = value === LunchBreakOption.Paid;

        if (value === LunchBreakOption.None) {
            newSettings.dailyBreakDuration = 0;
            newSettings.stdShiftDuration = 0;
        }

        setSettings(newSettings);
    };

    const handleRoundingCheckboxChange = (param: string) => {
        const currentValue = settings[param];
        const newValue = !currentValue;
        const newSettings = { ...settings };
        if (param === "isResultRoundingMinutesOn") {
            newSettings.isResultRoundingMinutesOn = newValue;
        } else if (param === "isShiftRoundingMinutesOn") {
            newSettings.isShiftRoundingMinutesOn = newValue;
        }
        setSettings(newSettings);
    };

    const handleRoundingSelectChange = (param: string, value: number) => {
        handleChange(param, value);
        if (param === "resultRoundingMinutes") {
            setSelectedResultRoundingMinutes(value);
        } else if (param === "shiftRoundingMinutes") {
            setSelectedShiftRoundingMinutes(value);
        }
    };

    return (
        <Grid2 container direction="column" p={3} spacing={2}>
            <Grid2 xs={12}>
                <Typography variant="h3">
                    {translate("workShifts")}
                </Typography>
            </Grid2>
            <Grid2 container direction="row" spacing={2} flexWrap="nowrap">
                <Grid2
                    container
                    direction="column"
                    spacing={2}
                    maxWidth={300}
                    flexShrink={0}
                >
                    <Grid2>
                        <NumberInput
                            id="paidBreak"
                            value={paidBreak}
                            className="field-width-normal"
                            label={translate("paidBreak")}
                            onChange={(val) => handleChange("paidBreak", val)}
                            endAdornment="min"
                        />
                    </Grid2>
                    {paidBreak > 0 ? (
                        <Grid2 sx={{ pl: 6 }}>
                            <NumberInput
                                id="paidBreakShiftMinMinutes"
                                value={paidBreakShiftMinMinutes}
                                label={translate("paidBreakShiftMinMinutes")}
                                onChange={(val) =>
                                    handleChange("paidBreakShiftMinMinutes", val)
                                }
                                endAdornment="min"
                                fullWidth
                            />
                        </Grid2>
                    ) : null}
                    <Grid2>
                        <NumberInput
                            id="paidWaiting"
                            value={paidWaiting}
                            className="field-width-normal"
                            label={translate("paidWaiting")}
                            onChange={(val) => handleChange("paidWaiting", val)}
                            endAdornment="min"
                        />
                    </Grid2>
                    <Grid2>
                        <MuiSelect
                            label={translate("shiftEffectiveDateLabel")}
                            className="field-width-normal"
                            value={shiftEffectiveDateLogic}
                            onChange={(val) =>
                                handleChange("shiftEffectiveDateLogic", val)
                            }
                            options={Object.keys(ShiftEffectiveDateLogic)
                                .filter((key) => isNaN(parseInt(key)))
                                .map((key) => ({
                                    value: ShiftEffectiveDateLogic[key],
                                    label: translate(
                                        `shiftEffectiveDateOption.${key}`
                                    ),
                                }))}
                        />
                    </Grid2>
                </Grid2>
                <Grid2
                    container
                    direction="column"
                    spacing={2}
                    marginLeft={5}
                    maxWidth={500}
                >
                    <Grid2>
                        <label>
                            <Checkbox
                                id="isSplitWorkShiftOnSalaryPeriodChange"
                                checked={isSplitWorkShiftOnSalaryPeriodChange}
                                onChange={(event) =>
                                    handleChange(
                                        "isSplitWorkShiftOnSalaryPeriodChange",
                                        event.target.checked
                                    )
                                }
                            />
                            {translate("isSplitWorkShiftOnSalaryPeriodChange")}
                        </label>
                        <Grid2>
                            <Alert severity="info">
                                {translate(
                                    "iisSplitWorkShiftOnSalaryPeriodChangeDescription"
                                )}
                            </Alert>
                        </Grid2>
                    </Grid2>
                    <Grid2>
                        <label>
                            <Checkbox
                                id="isWorkShiftStartBasedOnPlannedShift"
                                checked={isWorkShiftStartBasedOnPlannedShift}
                                onChange={(event) =>
                                    handleChange(
                                        "isWorkShiftStartBasedOnPlannedShift",
                                        event.target.checked
                                    )
                                }
                            />
                            {translate("isWorkShiftStartBasedOnPlannedShift")}
                        </label>
                        <Grid2>
                            <Alert severity="info">
                                {translate(
                                    "isWorkShiftStartBasedOnPlannedShiftDescription"
                                )}
                            </Alert>
                        </Grid2>
                    </Grid2>
                </Grid2>
                <Grid2
                    container
                    direction="column"
                    spacing={2}
                    marginLeft={5}
                    maxWidth={350}
                    flexShrink={0}
                >
                    <Typography variant="h6">
                        {translate("lunchBreak")}
                    </Typography>
                    <Grid2 container className="field-width-normal">
                        <Grid2 flex={12}>
                            <MuiSelect
                                fullWidth={true}
                                value={lunchBreakOption}
                                onChange={handleLunchOptionChange}
                                options={Object.keys(LunchBreakOption).map(
                                    (key) => ({
                                        value: LunchBreakOption[key],
                                        label: translate(
                                            `lunchBreakOption.${LunchBreakOption[key]}`
                                        ),
                                    })
                                )}
                            />
                        </Grid2>
                        <Grid2 mt={1} xs={true}>
                            <Tooltip
                                title={translate(
                                    `lunchBreakHelper.${lunchBreakOption}`
                                )}
                            >
                                <IconButton disableRipple>
                                    <InfoIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                    </Grid2>
                    {lunchBreakOption !== LunchBreakOption.None ? (
                        <>
                            <Grid2 className="field-width-normal">
                                <NumberInput
                                    id="dailyBreakDuration"
                                    value={dailyBreakDuration}
                                    label={translate("DAILYBREAKDURATION")}
                                    onChange={(val) =>
                                        handleChange("dailyBreakDuration", val)
                                    }
                                    endAdornment="min"
                                />
                            </Grid2>
                            <Grid2 className="field-width-normal">
                                <NumberInput
                                    id="stdShiftDuration"
                                    value={stdShiftDuration}
                                    label={translate("STDSHIFTDURATION")}
                                    onChange={(val) =>
                                        handleChange("stdShiftDuration", val)
                                    }
                                    endAdornment="min"
                                />
                            </Grid2>
                        </>
                    ) : null}
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        {translate("rounding")}
                    </Typography>
                    <Grid2>
                        <Box sx={{ width: 300 }}>
                            <Grid2
                                container
                                rowSpacing={2}
                                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                                alignItems="center"
                            >
                                <Grid2 xs={2}>
                                    <Checkbox
                                        id="RESULTROUNDINGMINUTESON"
                                        checked={settings.isResultRoundingMinutesOn}
                                        onClick={() =>
                                            handleRoundingCheckboxChange(
                                                "isResultRoundingMinutesOn"
                                            )
                                        }
                                    />
                                </Grid2>
                                {settings.isResultRoundingMinutesOn ? (
                                    <Grid2 xs={10}>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <MuiSelect
                                                label={translate("resultRounding")}
                                                value={settings.resultRoundingMinutes}
                                                options={roundingOptions}
                                                onChange={(value) =>
                                                    handleRoundingSelectChange(
                                                        "resultRoundingMinutes",
                                                        Number(value)
                                                    )
                                                }
                                            />
                                            <Box marginLeft={2}>
                                                <Tooltip
                                                    title={t(
                                                        "resultRoundingInfo",
                                                        {
                                                            selectedResultRoundingMinutes,
                                                        }
                                                    )}
                                                >
                                                    <InfoIcon color="primary" />
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Grid2>
                                ) : (
                                    <Grid2 xs={10}>
                                        {translate("roundResult")}
                                    </Grid2>
                                )}
                                <Grid2 xs={2}>
                                    <Checkbox
                                        id="SHIFTROUNDINGMINUTESON"
                                        checked={settings.isShiftRoundingMinutesOn}
                                        onClick={() =>
                                            handleRoundingCheckboxChange(
                                                "isShiftRoundingMinutesOn"
                                            )
                                        }
                                    />
                                </Grid2>
                                {settings.isShiftRoundingMinutesOn ? (
                                    <Grid2 xs={10}>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <MuiSelect
                                                label={translate("shiftRounding")}
                                                value={settings.shiftRoundingMinutes}
                                                options={roundingOptions}
                                                onChange={(value) =>
                                                    handleRoundingSelectChange(
                                                        "shiftRoundingMinutes",
                                                        Number(value)
                                                    )
                                                }
                                            />
                                            <Box marginLeft={2}>
                                                <Tooltip
                                                    title={t(
                                                        "shiftRoundingInfo",
                                                        {
                                                            selectedShiftRoundingMinutes,
                                                        }
                                                    )}
                                                >
                                                    <InfoIcon color="primary" />
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Grid2>
                                ) : (
                                    <Grid2 xs={10}>
                                        {translate("roundShift")}
                                    </Grid2>
                                )}
                            </Grid2>
                        </Box>
                    </Grid2>
                </Grid2>
            </Grid2>
        </Grid2>
    );
};
